import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { ENVIRONNEMENT, modeMaquette } from '../config';
import { Bouton } from '../components/Boutons';
import { Saisie } from '../components/Champs';
import { MessageErreur } from '../components/MessageErreur';
import { useConnexion, useOperateur } from '../auth/useSession';
import { LIBELLE_ROLE_INTERNE } from '../i18n/fr';
import type { RoleInterne } from '@genviz/shared/enums';

interface CompteDemo {
  email: string;
  mot_de_passe: string;
  role: RoleInterne;
}

/**
 * Connexion des opérateurs internes.
 *
 * Elle n'emprunte PAS le point d'entrée de l'application mobile : le rôle STAFF est porté par
 * des comptes séparés (D-09) et ne doit jamais pouvoir être obtenu depuis le parcours client.
 *
 * En mode maquette, les comptes de démonstration sont affichés : c'est un écran de
 * développement, masquer les identifiants d'un jeu factice ne protégerait rien et ferait perdre
 * du temps à chaque nouvel arrivant.
 */
export function ConnexionPage(): JSX.Element {
  const operateur = useOperateur();
  const emplacement = useLocation();
  const connexion = useConnexion();

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [comptes, setComptes] = useState<CompteDemo[]>([]);

  const maquette = modeMaquette();

  useEffect(() => {
    if (!maquette) return;
    // Import dynamique : le jeu de données factice n'est jamais embarqué en production.
    void import('../api/mock/serveur').then((module) => {
      setComptes(module.COMPTES_DEMONSTRATION);
    });
  }, [maquette]);

  if (operateur) {
    const etat = emplacement.state as { de?: string } | null;
    return <Navigate to={etat?.de ?? '/'} replace />;
  }

  const soumettre = (e: FormEvent): void => {
    e.preventDefault();
    connexion.mutate({ email: email.trim(), mot_de_passe: motDePasse });
  };

  return (
    <main className="gv-connexion">
      <form className="gv-connexion__boite" onSubmit={soumettre}>
        <div className="gv-ligne">
          <span className="gv-menu__logo" aria-hidden="true">
            C
          </span>
          <div className="gv-pile gv-pile--serree">
            <h1 className="gv-h2">Back-office CREB Connect</h1>
            <span className="gv-corps-s gv-secondaire">
              Supervision du parc · {ENVIRONNEMENT}
              {maquette ? ' · maquette' : ''}
            </span>
          </div>
        </div>

        <p className="gv-corps-s gv-secondaire">
          Accès réservé aux opérateurs internes. Les comptes clients n’ont pas cours ici.
        </p>

        <Saisie
          label="Adresse e-mail"
          type="email"
          name="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.currentTarget.value);
          }}
        />

        <Saisie
          label="Mot de passe"
          type="password"
          name="mot_de_passe"
          autoComplete="current-password"
          required
          value={motDePasse}
          onChange={(e) => {
            setMotDePasse(e.currentTarget.value);
          }}
        />

        {connexion.error ? <MessageErreur erreur={connexion.error} /> : null}

        <Bouton
          type="submit"
          variante="primaire"
          pleineLargeur
          occupe={connexion.isPending}
          disabled={email.trim() === '' || motDePasse === ''}
        >
          Se connecter
        </Bouton>

        {maquette && comptes.length > 0 ? (
          <div className="gv-bandeau gv-bandeau--info">
            <div className="gv-pile gv-pile--serree" style={{ minWidth: 0 }}>
              <strong>Mode maquette — aucun backend requis</strong>
              {comptes.map((c) => (
                <button
                  key={c.email}
                  type="button"
                  className="gv-bouton gv-bouton--petit"
                  onClick={() => {
                    setEmail(c.email);
                    setMotDePasse(c.mot_de_passe);
                  }}
                >
                  {c.email} · {LIBELLE_ROLE_INTERNE[c.role]}
                </button>
              ))}
              <span className="gv-corps-s">Mot de passe commun : {comptes[0]?.mot_de_passe}</span>
            </div>
          </div>
        ) : null}
      </form>
    </main>
  );
}
